import React, { useState, useEffect } from "react";
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { functions } from "../../Infrastructure/config";
import { httpsCallable } from "firebase/functions";
import { useSelector } from "react-redux";
import { Loader } from "../Common/Loader/loader";
import { Box, useTheme, Typography, Button, Avatar, Skeleton } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import { ValueText, FlexBox, HeadingText, checkoutSX } from "./style";
import { specialFont } from "../../Infrastructure/Theme/fontFamily";
import { classScheduleFormatWithoutTimezone } from "../../Infrastructure/Utilities/classScheduleFormat";
import useGetClassDate from "../../Infrastructure/CustomHooks/useGetClassDate";
import useAuthentication from "../../Infrastructure/States/onAuthStateChange";
import { ShowErrorToast } from "../Common/Toast/toast";
import { USER_LOGS_CLIENT } from "../../Infrastructure/Constants/logsConstant";
import ERROR_CONSTANTS from "../../Infrastructure/Constants/errorConstants";
import { ErrorView } from "../Common/ErrorComponents/errorView";
import CourseVector from '../../Assets/Images/courseVector.png'
import ProfileVector from "../../Assets/Images/vector.png";
import paths from "../../Infrastructure/Router/paths";
import useServerClock from "../../Infrastructure/CustomHooks/useServerClock";
import { safeClientLogEvent } from "../../Infrastructure/Utilities/safeClientLogEvent";

const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY
const stripePromise = loadStripe(stripePublicKey)

export const CheckoutForm = () => {
  const theme = useTheme();
  const navigate = useNavigate()
  const location = useLocation();
  const serverTime = useServerClock()
  const { userId } = useAuthentication()
  const baseURL = window.location.origin;
  const ip = useSelector((state) => state.ip.address)
  const page = useSelector((state) => state.route.currentRoute);
  const {
    course_id = "",
    educator_id = "",
    classId = "",
  } = location.state || {}; // Ensure no error occurs if `location.state` is null
  const [course, setCourse] = useState(null);
  const [uuid] = useState(Date.now().toString())
  const [classSchedule, setClassSchedule] = useState(null);
  const [courseImageError, setCourseImageError] = useState(false)
  const courses = useSelector((state) => state.userData?.coursesData);
  const educatorFirstName = course?.user?.account?.first_name
  const educatorLastName = course?.user?.account?.last_name
  const profileUrl = course?.user?.educator?.url
  const courseUrl = course?.url
  const educator = educatorFirstName + " " + educatorLastName
  const courseImage = course?.courseDetails?.details?.course_image
  const cost = course?.courseDetails?.basics?.cost
  const course_title = course?.courseDetails?.basics?.title
  const times = classSchedule?.times
  const first_class = classSchedule?.first_class
  const last_class = classSchedule?.last_class
  const duration = classSchedule?.duration
  const cohort_id = classSchedule?.cohort
  const classExpirationTime = classSchedule?.class_expiration_time
  const isCourseFee = course?.fee
  const avatar = course?.user?.educator?.profile?.avatar ? `${course?.user?.educator?.profile?.avatar}?t=${Date.now()}` : ProfileVector
  const [imageLoaded, setImageLoaded] = useState(false)
  const [courseImageLoaded, setCourseImageLoaded] = useState(false);
  let price = parseInt(cost)
  const [error, setError] = useState(null)
  const [loader, setLoader] = useState(true)
  const [clientSecret, setClientSecret] = useState(null)
  const [fetchingClientSecret, setFetchingClientSecret] = useState(false)
  const loading = useSelector((state) => state.userData.loading)
  const formatTimes = classScheduleFormatWithoutTimezone(times)
  const firstClass = useGetClassDate(first_class);
  const lastClass = useGetClassDate(last_class);
  const findCourseById = (courseId, courses) => {
    return courses?.[courseId] || null;
  }
  const findClassById = (classId, course) => {
    if (!course || !course.courseDetails || !course.courseDetails.class_schedule) {
      return null;
    }
    const classDetails = course.courseDetails.class_schedule[classId];
    if (classDetails) {
      return classDetails;
    } else {
      return null;
    }
  }
  useEffect(() => {
    // Added conditions to ensure the effect only runs when necessary
    if (userId && courses && course_id && classId) {
    Promise.resolve()
      .then(() => {
        // Step 2: Find the course by ID
        if (courses) {
          const foundCourse = findCourseById(course_id, courses);
          setCourse(foundCourse);
          return foundCourse;
        } else {
          throw new Error("Courses not available.");
        }
      })
      .then((foundCourse) => {
        // Step 3: Find the class schedule by ID
        if (classId && foundCourse) {
          const foundClass = findClassById(classId, foundCourse);
          setClassSchedule(foundClass);
          setLoader(false)
        }
      })
      .catch(async (error) => {
        setLoader(false)
        // Error handling
        await safeClientLogEvent({
          ...USER_LOGS_CLIENT.COURSE_CLASS_NOT_FOUND,
          uid: userId || null,
          source: "checkoutForm.jsx",
          emailInfo: null,
          metadata: null,
          error: error?.message || null
        });
      });
    }
  }, [courses, course_id, classId, userId, ip]);
  // Redirect to /courses if location.state is missing
  useEffect(() => {
    if (!location.state) {
      ShowErrorToast("Something went wrong. Try again.")
      navigate(paths.coursesPage.path);
      safeClientLogEvent({
        ...USER_LOGS_CLIENT.CHECKOUT_STATE_MISSING,
        uid: userId || null,
        source: "checkoutForm.jsx",
        emailInfo: null,
        metadata: { course_id, classId, educator_id },
        error: null
      });
    }
  }, [location.state, navigate, userId, ip, course_id, classId, educator_id ])

  useEffect(() => {
    if (!course || !classSchedule || !price) return;
    const fetchClientSecret = async () => {
      try {
        setFetchingClientSecret(true)
        let amountInDollars = price;
        let feeAmountInDollars = 0;
        if (isCourseFee) {
          const extraPercentage = 0.1;
          const platformFee = amountInDollars * extraPercentage;
          amountInDollars += platformFee
          feeAmountInDollars = platformFee
        } else {
          feeAmountInDollars = 0
        }
        const amountInCents = Math.round(amountInDollars * 100);
        const feeAmountInCents = Math.round(feeAmountInDollars * 100);
        const data = {
          ip,
          page,
          uuid,
          classId,
          course_id,
          educator_id,
          amount: amountInCents,
          application_fee_amount: feeAmountInCents,
          app_base_url: baseURL
        };
        const checkout = httpsCallable(functions, "checkout");
        const res = await checkout(data);
        if (res?.data?.clientSecret) {
          setClientSecret(res?.data?.clientSecret)
          setFetchingClientSecret(false)
        } else {
          if (res?.data?.errorMessage === "Already enrolled in this course.") {
            ShowErrorToast(res?.data?.errorMessage)
          }
          await safeClientLogEvent({
            ...USER_LOGS_CLIENT.CHECKOUT_NOT_AVAILABLE,
            uid: userId || null,
            source: "checkoutForm.jsx",
            emailInfo: null,
            metadata: null,
            error: res?.data?.errorMessage || null
          });
          setError(ERROR_CONSTANTS.CHECKOUT_ERROR_VIEW)
          setFetchingClientSecret(false)
          setLoader(false)
        }
      } catch (error) {
        setError(error?.message)
        await safeClientLogEvent({
          ...USER_LOGS_CLIENT.CHECKOUT_NOT_AVAILABLE,
          uid: userId || null,
          source: "CheckoutForm.jsx",
          emailInfo: null,
          metadata: null,
          error: error?.message || null
        });
        setFetchingClientSecret(false)
      }
    }
    fetchClientSecret()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ip, uuid, classId, course_id, educator_id, baseURL, price, isCourseFee, userId, course, classSchedule])

  // prevent learner to enroll in expire class
  useEffect(() => {
    const handleClassExpiration = async () => {
      if (!serverTime) {
        setError(ERROR_CONSTANTS.CHECKOUT_ERROR_VIEW);
        await safeClientLogEvent({
          ...USER_LOGS_CLIENT.SERVER_TIME_NOT_RETRIEVED,
          uid: userId || null,
          source: "checkoutForm.jsx",
          emailInfo: null,
          metadata: { classId },
          error: null
        });
        return;
      }
      if(course?.url === undefined || course?.url === null) {
        setError(ERROR_CONSTANTS.CHECKOUT_ERROR_VIEW);
        await safeClientLogEvent({
          ...USER_LOGS_CLIENT.COURSE_URL_NOT_AVAILABLE,
          uid: userId || null,
          source: "checkoutForm.jsx",
          emailInfo: null,
          metadata: { classId },
          error: null
        });
        navigate(paths.coursesPage.path);
        return;
      }
      // Check if the class has expired
      if (serverTime > classExpirationTime) {
        ShowErrorToast("This class has expired and cannot be enrolled.");
        await safeClientLogEvent({
          ...USER_LOGS_CLIENT.CLASS_EXPIRED,
          uid: userId || null,
          source: "checkoutForm.jsx",
          emailInfo: null,
          metadata: { classId },
          error: null
        });
        navigate(paths.coursesPage.path + `/${course.url}` || paths.coursesPage.path)
      }
    };
    if (classExpirationTime) {
      handleClassExpiration();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first_class, times, navigate, classExpirationTime, course, userId, ip, classId, serverTime, classSchedule]);
  
  const isLoading = loading || loader ||fetchingClientSecret || !course || !classSchedule || !clientSecret
  if (!error && isLoading) {
    return (
      <Box height="100vh">
        <Loader />
      </Box>
    );
  }
  return (
    <Box sx={checkoutSX.page}>
      <Box sx={checkoutSX.wrapper}>
        <Box sx={checkoutSX.center}>
          <Box sx={checkoutSX.content}>
            <Box sx={checkoutSX.headerRow}>
              <Box sx={checkoutSX.courseInfo}>
                <Box onClick={() => navigate(paths.coursesPage.path + `/${courseUrl}`)} sx={checkoutSX.courseImageBox}>
                  {!courseImageLoaded && (<Skeleton variant="rectangular" sx={checkoutSX.skeletonRect} />)}
                  <img
                    src={courseImageError ? CourseVector : courseImage}
                    alt="course"
                    style={{
                      display: courseImageLoaded ? "block" : "none",
                      height: "100%",
                      width: "100%",
                      objectFit: "cover",
                      borderRadius: "10px",
                    }}
                    onLoad={() => setCourseImageLoaded(true)}
                    onError={() => {
                      setCourseImageError(true);
                      setCourseImageLoaded(true);
                    }}
                  />
                </Box>
                <Typography
                  sx={checkoutSX.courseTitle}
                  fontFamily={specialFont}
                  color="primary"
                >
                  {course_title}
                </Typography>
              </Box>
              <Box sx={checkoutSX.educatorBox}>
                <Typography sx={checkoutSX.educatorName} color="secondary">
                  By {educator}
                </Typography>
                <Box>
                  {!imageLoaded && <Skeleton variant="circular" width={40} height={40} />}
                  <Avatar
                    src={avatar}
                    onClick={() => navigate(paths.educators.path + `/${profileUrl}`)}
                    onLoad={() => setImageLoaded(true)}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = ProfileVector;
                    }}
                    style={!imageLoaded ? { display: "none" } : {}}
                    sx={checkoutSX.avatar}
                  />
                </Box>
              </Box>
            </Box>
            <Grid container sx={{ ...checkoutSX.scheduleGrid, border: `3px solid ${theme.palette.primary.main}` }}>
              <Grid xs={12} sm={6} lg={6} xl={6}>
                <FlexBox mb={{ xs: 1 }}>
                  <ValueText sx={{ textDecoration: "underline" }}>Class Schedule</ValueText>
                </FlexBox>
                <Box display={'flex'} flexDirection={'column'}>
                  {formatTimes?.map((time, index) => (
                    <HeadingText key={index}>{time}</HeadingText>
                  ))}
                </Box>
              </Grid>
              <Grid sx={checkoutSX.gridRightColumn} xs={11} sm={6} lg={6} xl={6}>
                <Box sx={checkoutSX.hrBox}>
                  <hr style={{ width: '60%' }} />
                </Box>
                <FlexBox>
                  <HeadingText>Duration:</HeadingText>
                  {/* eslint-disable-next-line eqeqeq */}
                  <ValueText>{duration}{" "}{duration == "1" ? "Week" : "Weeks"}</ValueText>
                </FlexBox>
                <FlexBox>
                  <HeadingText>First Day of Class:</HeadingText>
                  <ValueText>{firstClass?.month}/{firstClass?.weekday}/{firstClass?.year}</ValueText>
                </FlexBox>
                <FlexBox>
                  <HeadingText>Last Day of Class:</HeadingText>
                  <ValueText>{lastClass?.month}/{lastClass?.weekday}/{lastClass?.year}</ValueText>
                </FlexBox>
                <FlexBox>
                  <HeadingText>Cohort:</HeadingText>
                  <ValueText>{cohort_id}</ValueText>
                </FlexBox>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box sx={checkoutSX.bottomSection}>
          {error ? (
            <Box sx={checkoutSX.errorBox}>
              <ErrorView />
              <Button variant="contained" onClick={() => navigate(paths.contactPage.path)}>
                Contact
              </Button>
            </Box>
          ) : (
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </Box>
      </Box>
    </Box>
  );
};

